import {
  Conversation,
  CreateConversation,
  CreateMessage,
  DownloadConversationResponse,
  Message,
  UpdateConversation,
} from '../Types/conversation';
import { API_URL, enrichedFetch, reportError, streamFetch } from './api';

export const getConversations = (): Promise<Conversation[]> => {
  return enrichedFetch(`${API_URL}/conversations`) as Promise<Conversation[]>;
};

export const getConversation = (id: Conversation['id']): Promise<Conversation> => {
  return enrichedFetch(`${API_URL}/conversations/${id}`) as Promise<Conversation>;
};

export const postConversation = (conversation: CreateConversation): Promise<Conversation> => {
  return enrichedFetch(`${API_URL}/conversations`, {
    method: 'POST',
    body: JSON.stringify(conversation),
    headers: {
      'Content-Type': 'application/json',
    },
  }) as Promise<Conversation>;
};

export const updateConversation = async (
  updatedFields: UpdateConversation,
  conversationId: Conversation['id']
): Promise<void> => {
  await enrichedFetch(`${API_URL}/conversations/${conversationId}`, {
    method: 'PUT',
    body: JSON.stringify(updatedFields),
    headers: {
      'Content-Type': 'application/json',
    },
  });
};

export const deleteConversation = async (conversationId: Conversation['id']): Promise<void> => {
  await enrichedFetch(`${API_URL}/conversations/${conversationId}`, {
    method: 'DELETE',
  });
};

export const getMessages = (conversationId: Conversation['id']): Promise<Message[]> => {
  return enrichedFetch(`${API_URL}/conversations/messages/${conversationId}`) as Promise<Message[]>;
};

// Sends a message and streams the AI response; onDelta receives each chunk of text.
// Resolves with the saved AI message, or undefined if the request failed.
export const streamMessage = async (
  message: CreateMessage,
  llm: string,
  onDelta: (delta: string) => void
): Promise<Message | undefined> => {
  let savedMessage: Message | undefined;
  await streamFetch(
    `${API_URL}/conversations/messages/stream`,
    {
      method: 'POST',
      body: JSON.stringify({ message, llm }),
      headers: {
        'Content-Type': 'application/json',
      },
    },
    (event, data) => {
      if (event === 'delta') {
        onDelta(data as string);
      } else if (event === 'done') {
        savedMessage = data as Message;
      } else if (event === 'error') {
        reportError((data as { message: string }).message, 500);
      }
    }
  );
  return savedMessage;
};

export const downloadFile = (
  conversationId: Conversation['id']
): Promise<DownloadConversationResponse> => {
  return enrichedFetch(`${API_URL}/conversations/download/${conversationId}`, {
    method: 'GET',
  }) as Promise<DownloadConversationResponse>;
};
